import { useState, useEffect } from 'react'
import { useModal } from '../../contexts/ModalContext'
import { useCampaign } from '../../contexts/CampaignContext'
import { useAuth } from '../../contexts/AuthContext'
import AdminControls from '../AdminControls'
import NPCForm from '../forms/NPCForm'
import MarkdownText from '../MarkdownText'
import DescriptionSections from '../DescriptionSections'
import SortSelect, { usePersistedSortMode } from '../SortSelect'
import ViewToggle, { usePersistedViewMode } from '../ViewToggle'
import EntityTable from '../EntityTable'
import { sortEntities } from '../../utils/entitySort'
import { parseSections, serializeSections } from '../../utils/descriptionSections'

// Distinguishes individual NPCs from organizations/Factions (npcs.entity_type).
const NPC_TYPE_META = {
  faction: { icon: '🏛️', label: 'Faction' },
  npc: { icon: '👤', label: 'NPC' },
}
const npcTypeMeta = (entityType) => NPC_TYPE_META[entityType] || NPC_TYPE_META.npc

const typeBadgeStyle = {
  display: 'inline-block',
  background: 'rgba(42, 85, 102, 0.4)',
  color: 'var(--accent-cyan)',
  padding: '0.1rem 0.45rem',
  borderRadius: '4px',
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.03em',
}

function NPCsTab({ focusId, onConsumeFocus }) {
  const [npcs, setNpcs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingNPC, setEditingNPC] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedNPC, setSelectedNPC] = useState(null)
  const [filterText, setFilterText] = useState('')
  const [sortMode, setSortMode] = usePersistedSortMode('sort:npcs')
  const [viewMode, setViewMode] = usePersistedViewMode('view:npcs')
  const { canEdit } = useCampaign()
  const { alert: showAlert, confirm } = useModal()
  const { authEpoch } = useAuth()

  // authEpoch: see the identical comment in ItemsTab.jsx.
  useEffect(() => {
    fetchNPCs()
  }, [authEpoch])

  // Open a specific NPC when navigated to via a [[wiki-link]].
  useEffect(() => {
    if (!focusId || loading) return
    const target = npcs.find((n) => n.id === focusId)
    if (target) {
      setSelectedNPC(target)
      onConsumeFocus?.()
    }
  }, [focusId, loading, npcs])

  useEffect(() => {
    if (!selectedNPC) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedNPC(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [selectedNPC])

  const fetchNPCs = () => {
    // See ItemsTab.jsx's fetchItems for why the res.ok check and array guard
    // are here: a non-OK response still has a JSON body, and a successful
    // retry must clear any previous error.
    fetch('/api/npcs')
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load NPCs'))))
      .then(data => {
        setNpcs(Array.isArray(data) ? data : [])
        setError(null)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }

  const handleEdit = (npc) => {
    setEditingNPC(npc)
  }

  const handleCreate = () => {
    setIsCreating(true)
  }

  const handleFormSubmit = async (formData, avatarFile, deleteAvatar) => {
    try {
      const isEdit = !!editingNPC
      const response = await fetch('/api/npcs', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? { ...formData, id: editingNPC.id } : formData)
      })

      if (response.ok) {
        const result = await response.json()

        // Get the NPC ID (either from editing or from the newly created NPC)
        const npcId = isEdit ? editingNPC.id : result.id

        // Handle avatar deletion
        if (deleteAvatar) {
          const deleteResponse = await fetch('/api/npcs/delete-avatar', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ npc_id: npcId })
          })

          if (!deleteResponse.ok) {
            console.error('Failed to delete avatar')
          }
        }
        // Handle avatar upload
        else if (avatarFile) {
          const uploadFormData = new FormData()
          uploadFormData.append('avatar', avatarFile)
          uploadFormData.append('npc_id', npcId)

          const uploadResponse = await fetch('/api/npcs/upload-avatar', {
            method: 'POST',
            body: uploadFormData
          })

          if (!uploadResponse.ok) {
            const error = await uploadResponse.json()
            showAlert({
              title: 'Warning',
              message: `NPC saved but avatar upload failed: ${error.error}`,
              type: 'error'
            })
          }
        }

        await fetchNPCs()
        showAlert({
          title: isEdit ? 'NPC Updated' : 'NPC Created',
          message: `NPC "${formData.name}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
          type: 'success'
        })
        setEditingNPC(null)
        setIsCreating(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || `Failed to ${isEdit ? 'update' : 'create'} NPC`,
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: `Failed to ${editingNPC ? 'update' : 'create'} NPC`,
        type: 'error'
      })
    }
  }

  const handleDelete = async (npc) => {
    await confirm({
      title: 'Delete NPC',
      message: `Are you sure you want to delete "${npc.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/npcs', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: npc.id })
          })

          if (response.ok) {
            setNpcs(npcs.filter(n => n.id !== npc.id))
            showAlert({
              title: 'NPC Deleted',
              message: `"${npc.name}" has been deleted.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to delete NPC',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to delete NPC',
            type: 'error'
          })
        }
      }
    })
  }

  const handleToggleVisibility = async (npc) => {
    const action = npc.is_visible ? 'hide' : 'reveal'
    await confirm({
      title: `${action === 'hide' ? 'Hide' : 'Reveal'} NPC`,
      message: `${action === 'hide' ? 'Hide' : 'Reveal'} "${npc.name}" ${action === 'hide' ? 'from' : 'to'} players?`,
      confirmText: action === 'hide' ? 'Hide' : 'Reveal',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/npcs', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: npc.id, is_visible: npc.is_visible ? 0 : 1 })
          })

          if (response.ok) {
            setNpcs(npcs.map(n =>
              n.id === npc.id ? { ...n, is_visible: npc.is_visible ? 0 : 1 } : n
            ))
            showAlert({
              title: 'Visibility Updated',
              message: `"${npc.name}" is now ${action === 'hide' ? 'hidden from' : 'visible to'} players.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to update visibility',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to update visibility',
            type: 'error'
          })
        }
      }
    })
  }

  const handleSetRevealed = async (index, revealed) => {
    const current = parseSections(selectedNPC.description_sections)
    const priorRevealed = current[index]?.revealed
    const next = current.map((s, i) => (i === index ? { ...s, revealed } : s))
    const payload = serializeSections(next)

    // Optimistic: the pill flips immediately and is put back if the write
    // fails, so a DM never sees a reveal the server refused.
    setSelectedNPC((prev) => ({ ...prev, description_sections: payload }))
    setNpcs((prev) => prev.map((n) => (n.id === selectedNPC.id ? { ...n, description_sections: payload } : n)))

    try {
      const res = await fetch('/api/npcs', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedNPC.id, description_sections: payload }),
      })
      if (!res.ok) throw new Error('Failed to update reveal')
    } catch (err) {
      // Revert only this index, re-parsed from whatever state is current at
      // revert time (not the stale `current` snapshot from above) — a second
      // flip of a different index may have already landed optimistically
      // while this request was in flight, and restoring the whole pre-flip
      // array would silently discard it.
      const revertOnlyThisIndex = (sections) =>
        serializeSections(
          parseSections(sections).map((s, i) => (i === index ? { ...s, revealed: priorRevealed } : s))
        )
      setSelectedNPC((prev) => ({ ...prev, description_sections: revertOnlyThisIndex(prev.description_sections) }))
      setNpcs((prev) =>
        prev.map((n) =>
          n.id === selectedNPC.id ? { ...n, description_sections: revertOnlyThisIndex(n.description_sections) } : n
        )
      )
      showAlert({ title: 'Error', message: err.message, type: 'error' })
    }
  }

  if (loading) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>NPCs & Factions</h1>
          <p className="page-subtitle">Characters and factions you've met on your journey</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading NPCs...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>NPCs & Factions</h1>
          <p className="page-subtitle">Characters and factions you've met on your journey</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: '#e74c3c' }}>Error loading NPCs: {error}</p>
        </div>
      </div>
    )
  }

  if (npcs.length === 0) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>NPCs & Factions</h1>
          <p className="page-subtitle">Characters and factions you've met on your journey</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>👥</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No NPCs Encountered</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: canEdit ? '1.5rem' : 0 }}>
            {canEdit ? 'Add your first NPC or faction to get this campaign started.' : "Meet characters throughout your journey and they'll be recorded here."}
          </p>
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New NPC or Faction
            </button>
          )}
        </div>

        <NPCForm
          isOpen={isCreating || !!editingNPC}
          onClose={() => {
            setIsCreating(false)
            setEditingNPC(null)
          }}
          npc={editingNPC}
          onSubmit={handleFormSubmit}
        />
      </div>
    )
  }

  const trimmedFilter = filterText.trim()
  const isSearching = trimmedFilter.length > 0

  // Previously unsorted, so the list came out in the API's created_at DESC
  // order. The old comparator's only rule pushed hidden-from-players rows to
  // the end, which was unreachable: the filter below already drops every
  // hidden row whenever !canEdit, so both sides were always false.
  const filteredNPCs = sortEntities(
    npcs.filter((npc) => {
      // Players (and admins previewing as a player) never see hidden NPCs at all.
      if (!canEdit && !npc.is_visible) return false
      if (!isSearching) return true
      const searchTerm = trimmedFilter.toLowerCase()
      const name = npc.name?.toLowerCase() || ''
      const title = npc.title?.toLowerCase() || ''
      const description = npc.description?.toLowerCase() || ''
      let tagsText = ''
      if (npc.tags) {
        try {
          tagsText = JSON.parse(npc.tags).join(' ').toLowerCase()
        } catch {
          tagsText = ''
        }
      }

      return (
        name.includes(searchTerm) ||
        title.includes(searchTerm) ||
        description.includes(searchTerm) ||
        tagsText.includes(searchTerm)
      )
    }),
    sortMode
  )

  // Table columns. Only 'name' and 'desc' are flexible; every other role is
  // sized to its own content by EntityTable.css, which matters most here —
  // "Kind" is one of exactly two badges and must not claim a share of the
  // width that the description needs.
  const npcColumns = [
    {
      key: 'avatar',
      header: '',
      role: 'lead',
      render: (npc) => {
        const isHidden = !npc.is_visible && !canEdit
        return (
          <div className={`et-thumb${isHidden ? ' is-unknown' : ''}`}>
            {isHidden ? '??' : (
              npc.avatar_url
                ? <img src={npc.avatar_url} alt={npc.name} />
                : (npc.avatar_initials || '??')
            )}
          </div>
        )
      },
    },
    {
      key: 'name',
      header: 'Name',
      role: 'name',
      render: (npc) => {
        const isHidden = !npc.is_visible && !canEdit
        return (
          <>
            <div className="et-name">
              {isHidden ? 'Unknown NPC' : npc.name}
              {canEdit && !npc.is_visible && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Hidden</span>
              )}
            </div>
            {/* The title is prose, not a facet, so it sits under the name
                rather than claiming a column of its own. */}
            {!isHidden && npc.title && <div className="et-sub">{npc.title}</div>}
          </>
        )
      },
    },
    {
      key: 'kind',
      header: 'Kind',
      role: 'meta',
      render: (npc) => {
        const isHidden = !npc.is_visible && !canEdit
        if (isHidden) return null
        return (
          <span style={typeBadgeStyle}>
            {npcTypeMeta(npc.entity_type).icon} {npcTypeMeta(npc.entity_type).label}
          </span>
        )
      },
    },
    {
      key: 'tags',
      header: 'Tags',
      role: 'meta',
      render: (npc) => {
        const isHidden = !npc.is_visible && !canEdit
        if (isHidden) return null
        let tags = []
        try {
          tags = npc.tags ? JSON.parse(npc.tags) : []
        } catch {
          tags = []
        }
        if (!Array.isArray(tags) || tags.length === 0) return null
        return (
          <div className="et-tagset">
            {tags.map((tag, i) => <span key={i} className="et-tag">{tag}</span>)}
          </div>
        )
      },
    },
    {
      key: 'description',
      header: 'Description',
      role: 'desc',
      render: (npc) => {
        const isHidden = !npc.is_visible && !canEdit
        return isHidden
          ? 'You have not met this person yet.'
          : <MarkdownText>{npc.description}</MarkdownText>
      },
    },
    {
      key: 'actions',
      header: '',
      role: 'actions',
      render: (npc) => canEdit && (
        <AdminControls
          item={npc}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleVisibility={handleToggleVisibility}
          type="NPC"
        />
      ),
    },
  ]

  return (
    <div className="tab-pane">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="page-header" style={{ marginBottom: 0, flexShrink: 0 }}>
          <h1>NPCs & Factions</h1>
          <p className="page-subtitle">Characters and factions you've met on your journey</p>
        </div>
        <input
          type="text"
          placeholder="Filter NPCs or factions by name, title, tags, or description..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            flex: '1 1 260px',
            minWidth: '200px',
            padding: '0.75rem 1rem',
            background: 'var(--accent-deep)',
            border: '1px solid var(--accent-teal)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            fontSize: '0.95rem',
            fontFamily: 'inherit'
          }}
        />
        <SortSelect id="npcs-sort" value={sortMode} onChange={setSortMode} />
        <ViewToggle value={viewMode} onChange={setViewMode} />
        {canEdit && (
          <button
            onClick={handleCreate}
            style={{
              background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
              color: '#ffffff',
              border: '1px solid #27ae60',
              padding: '0.8rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
            }}
          >
            + Add New NPC or Faction
          </button>
        )}
      </div>

      {viewMode === 'table' ? (
        <EntityTable
          rows={filteredNPCs}
          columns={npcColumns}
          label="NPCs and factions"
          onRowClick={setSelectedNPC}
          isDimmed={(npc) => !npc.is_visible}
          emptyMessage={isSearching ? 'No NPCs or factions match that filter.' : 'No NPCs or factions yet.'}
        />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
        {filteredNPCs.map(npc => {
          const tags = npc.tags ? JSON.parse(npc.tags) : []
          const isHidden = !npc.is_visible && !canEdit

          return (
            <div
              key={npc.id}
              onClick={() => setSelectedNPC(npc)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedNPC(npc)
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                background: 'var(--card-bg)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                display: 'flex',
                flexDirection: 'column',
                opacity: npc.is_visible ? 1 : 0.6,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 18px rgba(0, 0, 0, 0.25)'
                e.currentTarget.style.borderColor = 'var(--accent-teal)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = 'var(--border-color)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                {(isHidden || npc.avatar_url) && (
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: isHidden ? '#6b7f91' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', flexShrink: 0, overflow: 'hidden', border: isHidden ? 'none' : '2px solid var(--accent-teal)' }}>
                    {isHidden ? '??' : (
                      <img src={npc.avatar_url} alt={npc.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {!isHidden && (
                    <span style={{ ...typeBadgeStyle, marginBottom: '0.3rem' }}>
                      {npcTypeMeta(npc.entity_type).icon} {npcTypeMeta(npc.entity_type).label}
                    </span>
                  )}
                  <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0, marginBottom: '0.2rem', wordWrap: 'break-word' }}>
                    {isHidden ? 'Unknown NPC' : npc.name}
                    {canEdit && !npc.is_visible && <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Hidden</span>}
                  </h3>
                  {!isHidden && npc.title && (
                    <p style={{ color: 'var(--accent-teal)', fontSize: '0.85rem', margin: 0 }}>{npc.title}</p>
                  )}
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.9rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {isHidden ? 'You have not met this person yet.' : <MarkdownText>{npc.description}</MarkdownText>}
              </div>
              {!isHidden && tags.length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  {tags.map((tag, index) => (
                    <span key={index} style={{ background: 'rgba(42, 85, 102, 0.3)', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.75rem' }}>
                {!isHidden ? <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>(click to see more)</span> : <span />}
                {canEdit && (
                  <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                    <AdminControls
                      item={npc}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleVisibility={handleToggleVisibility}
                      type="NPC"
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      )}

      {selectedNPC && (() => {
        const isHidden = !selectedNPC.is_visible && !canEdit
        const hasImage = !isHidden && selectedNPC.avatar_url
        const tags = !isHidden && selectedNPC.tags ? JSON.parse(selectedNPC.tags) : []

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(5, 10, 18, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
                border: '2px solid var(--accent-teal)',
                borderRadius: '12px',
                maxWidth: '980px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                position: 'relative',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                // See SessionsTab for why padding moved onto the children.
                overflow: 'hidden'
              }}
            >
              {/* Non-scrolling header row — an absolutely-positioned Close
                  button would sit on top of the scroll wrapper's scrollbar. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1rem 0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => setSelectedNPC(null)}
                  style={{
                    background: 'linear-gradient(135deg, #3a4550 0%, #2d363f 100%)',
                    color: '#ffffff',
                    border: '1px solid #5a6570',
                    borderRadius: '8px',
                    padding: '0.6rem 1.4rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  aria-label="Close NPC details"
                >
                  Close
                </button>
              </div>

              <div style={{ overflowY: 'auto', minHeight: 0, padding: '0 1rem 2rem 2rem', scrollbarGutter: 'stable' }}>
              <div className="detail-float">
                {(isHidden || hasImage) && (
                <div
                  className="detail-float-media"
                  style={{
                    width: '320px',
                    height: '320px',
                    maxWidth: '80vw',
                    maxHeight: '60vh',
                    borderRadius: '50%',
                    background: isHidden ? '#6b7f91' : (hasImage ? 'transparent' : 'var(--accent-teal)'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '5rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    border: hasImage ? '2px solid var(--accent-teal)' : 'none',
                    flexShrink: 0
                  }}
                >
                  {isHidden ? '??' : (
                    <img src={selectedNPC.avatar_url} alt={selectedNPC.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </div>
                )}

                <div>
                  {!isHidden && (
                    <span style={{ ...typeBadgeStyle, marginBottom: '0.5rem' }}>
                      {npcTypeMeta(selectedNPC.entity_type).icon} {npcTypeMeta(selectedNPC.entity_type).label}
                    </span>
                  )}
                  <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                    {isHidden ? 'Unknown NPC' : selectedNPC.name}
                  </h2>
                  {!isHidden && selectedNPC.title && (
                    <p style={{ color: 'var(--accent-teal)', fontSize: '1rem', marginBottom: '1rem' }}>{selectedNPC.title}</p>
                  )}
                  <div style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '1rem', marginBottom: '1rem' }}>
                    {isHidden ? 'You have not met this person yet.' : <MarkdownText style={{ whiteSpace: 'pre-line' }}>{selectedNPC.full_description || selectedNPC.description}</MarkdownText>}
                  </div>
                  {!isHidden && (
                    <DescriptionSections
                      sections={selectedNPC.description_sections}
                      editable={canEdit}
                      onSetRevealed={handleSetRevealed}
                    />
                  )}
                  {!isHidden && tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {tags.map((tag, index) => (
                        <span key={index} style={{ background: 'rgba(42, 85, 102, 0.3)', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>
        )
      })()}

      <NPCForm
        isOpen={isCreating || !!editingNPC}
        onClose={() => {
          setIsCreating(false)
          setEditingNPC(null)
        }}
        npc={editingNPC}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}

export default NPCsTab
